import { computed, ref, type Ref } from "vue"
import { UseRootStore } from "@/model/RootStore"
import * as apiClient from "@/utils/ApiClient"

export type NotificationChannel = "in_app" | "email" | "sms"
export type NotificationFrequency = "immediate"

export type PreferenceViewModel = {
	channel: NotificationChannel
	enabled: boolean
	frequency: NotificationFrequency
}

export const immediateFrequency: NotificationFrequency = "immediate"

const supportedChannels: NotificationChannel[] = ["in_app", "email", "sms"]

const defaultPreferences: PreferenceViewModel[] = [
	{ channel: "in_app", enabled: true, frequency: immediateFrequency },
	{ channel: "email", enabled: false, frequency: immediateFrequency },
	{ channel: "sms", enabled: false, frequency: immediateFrequency },
]

const clonePreferences = (source: PreferenceViewModel[]): PreferenceViewModel[] =>
	source.map(preference => ({ ...preference }))
const serializePreferences = (source: PreferenceViewModel[]) => JSON.stringify(source)

const isNotificationChannel = (value: string): value is NotificationChannel =>
	supportedChannels.includes(value as NotificationChannel)

export function useNotificationPreferences(isSmsBlocked: Ref<boolean>, isSaving: Ref<boolean>) {
	const store = UseRootStore()

	const preferences = ref<PreferenceViewModel[]>(clonePreferences(defaultPreferences))
	const savedPreferences = ref<PreferenceViewModel[]>(clonePreferences(defaultPreferences))
	const isLoadingPreferences = ref(false)
	const preferencesError = ref<string | null>(null)

	const hasNotificationPendingChanges = computed(
		() => serializePreferences(preferences.value) !== serializePreferences(savedPreferences.value)
	)

	const defaultPreferenceForChannel = (channel: NotificationChannel): PreferenceViewModel => {
		const fallback = defaultPreferences.find(preference => preference.channel === channel)
		return { ...(fallback ?? { channel, enabled: false, frequency: immediateFrequency }) }
	}

	const mergePreferences = (apiPreferences: apiClient.NotificationPreference[]): PreferenceViewModel[] => {
		const preferencesByChannel = new Map<NotificationChannel, PreferenceViewModel>(
			defaultPreferences.map(preference => [preference.channel, { ...preference }])
		)

		for (const preference of apiPreferences) {
			if (!isNotificationChannel(preference.channel)) continue

			preferencesByChannel.set(preference.channel, {
				channel: preference.channel,
				enabled: preference.enabled,
				frequency: immediateFrequency,
			})
		}

		return supportedChannels.map(channel => preferencesByChannel.get(channel) ?? defaultPreferenceForChannel(channel))
	}

	const isPreferenceDisabled = (preference: PreferenceViewModel) =>
		isLoadingPreferences.value || isSaving.value || (preference.channel === "sms" && isSmsBlocked.value)

	const isPreferenceChecked = (preference: PreferenceViewModel) =>
		preference.channel === "sms" && isSmsBlocked.value ? false : preference.enabled

	const loadPreferences = async () => {
		if (!store.isUserLoggedIn) return

		isLoadingPreferences.value = true
		preferencesError.value = null
		try {
			const loadedPreferences = await apiClient.fetchNotificationPreferences(store.userApiToken)
			const mergedPreferences = mergePreferences(loadedPreferences)
			preferences.value = clonePreferences(mergedPreferences)
			savedPreferences.value = clonePreferences(mergedPreferences)
		} catch (error) {
			preferencesError.value = error instanceof Error ? error.message : String(error)
		} finally {
			isLoadingPreferences.value = false
		}
	}

	const setPreferenceEnabled = (preference: PreferenceViewModel, enabled: boolean) => {
		if (preference.channel === "sms" && enabled && isSmsBlocked.value) return

		preference.enabled = enabled
		preferencesError.value = null
	}

	const resetPreferences = () => {
		preferences.value = clonePreferences(savedPreferences.value)
		preferencesError.value = null
	}

	const saveNotificationPreferences = async () => {
		if (!hasNotificationPendingChanges.value) return

		const saved = await Promise.all(
			preferences.value.map(preference =>
				apiClient.setNotificationPreference(store.userApiToken, {
					channel: preference.channel,
					enabled: isPreferenceChecked(preference),
					frequency: immediateFrequency,
				})
			)
		)
		const mergedPreferences = mergePreferences(saved)
		preferences.value = clonePreferences(mergedPreferences)
		savedPreferences.value = clonePreferences(mergedPreferences)
	}

	return {
		preferences,
		savedPreferences,
		isLoadingPreferences,
		preferencesError,
		hasNotificationPendingChanges,
		isPreferenceDisabled,
		isPreferenceChecked,
		loadPreferences,
		setPreferenceEnabled,
		resetPreferences,
		saveNotificationPreferences,
	}
}
